import { Alert } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { getLanguage } from '@common/translate/translate';
import type { IProcess } from '../../interfaces/process';
import { ProcedureStatus } from '../../../../common/enums/procedureStatus';
import { ReportStatus } from '../../../../common/enums/reportStatus';
import { RequestStatus } from '../../../../common/enums/requestStatus';
import { ServiceType } from '../../../../common/enums/serviceType';
import { TaskType } from '../../../../common/enums/task';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { fetchJson } from '../../../store/fetch';
import { canChangeCareProvider } from '../../../utils/canChangeCareProvider';
import { ThirdHeader } from '../../elements/common';
import { Modal } from '../../elements/modals/Modal';
import { ChangeRequester } from './ChangeRequester';
import { ChangeServiceProvider } from './ChangeServiceProvider';
import { ChangeServiceSupervisor } from './ChangeServiceSupervisor';

interface IChangeCareproviderProps {
    onClose: () => Promise<void>;
    requestId: string;
    serviceId: string;
}

export function ChangeCareProviderModal(props: IChangeCareproviderProps) {
    const [process, setProcess] = useState<IProcess | undefined>();
    const reviewStatus = process?.diagnosticReports.find(() => true)?.reviewStatus;
    const hasDiagnosticReport = Boolean(process?.diagnosticReports?.length);

    const translate = getLanguage();

    useEffect(() => {
        load();
    }, []);

    return (
        <Modal
            actionButtons={[]}
            isOpen
            onClose={async () => await props.onClose()}
            size="medium"
            title={translate.request.assignTo}
        >
            {process && process.requesterPractitioner && process.requesterOrganization && (
                <>
                    {showRequestActiveAlert()}

                    <ThirdHeader text={getHeaderText()} />
                    <ChangeRequester
                        currentOrganizationId={process.requesterOrganizationId!}
                        currentRequesterId={process.requesterPractitionerId!}
                        process={process}
                        reload={async () => await load()}
                        serviceId={props.serviceId}
                    />
                    {canChangeBiometrist() && (
                        <>
                            <ThirdHeader
                                text={`${translate.serviceProvider.biometrician}
                                (${getPerformerPractitioner('biometrist').name}
                                ${
                                    getPerformerPractitioner('biometrist').role
                                        ? `, ${getPerformerPractitioner('biometrist').role}`
                                        : ''
                                })`}
                            />
                            <ChangeServiceProvider
                                currentOrganizationId={getPerformerPractitioner('biometrist').organizationId!}
                                currentPerformerId={getPerformerPractitioner('biometrist').id!}
                                dateSent={checkIsActivityEnded() ? process.dateSent : undefined}
                                procedureId={process.procedure?.id}
                                reload={async () => await load()}
                                requestId={process.id}
                                // eslint-disable-next-line jsx-a11y/aria-role
                                role="biometrist"
                                serviceId={props.serviceId}
                            />
                        </>
                    )}
                    {hasServiceProviderForGrading() && (
                        <>
                            <ThirdHeader
                                text={`${
                                    process.serviceType === ServiceType.examination
                                        ? translate.serviceProvider.grader
                                        : translate.serviceProvider.provider
                                }
                                (${getPerformerPractitioner('grader').name}
                                ${
                                    getPerformerPractitioner('grader').role
                                        ? `, ${getPerformerPractitioner('grader').role}`
                                        : ''
                                })`}
                            />
                            <ChangeServiceProvider
                                currentOrganizationId={getPerformerPractitioner('grader').organizationId!}
                                currentPerformerId={getPerformerPractitioner('grader').id!}
                                dateSent={checkIsActivityEnded() ? process.dateSent : undefined}
                                diagnosticReportId={
                                    process.diagnosticReports[0] ? process.diagnosticReports[0].id : undefined
                                }
                                reload={async () => await load()}
                                requestId={process.id}
                                role={process.serviceType === ServiceType.consultation ? 'provider' : 'grader'}
                                serviceId={props.serviceId}
                            />
                        </>
                    )}
                    {canChangeBiograder() && (
                        <>
                            <ThirdHeader
                                text={`${translate.serviceProvider.biometrician} ${translate.common.and} ${translate.serviceProvider.grader}
                                (${getPerformerPractitioner('biometrist').name}
                                ${
                                    getPerformerPractitioner('biometrist').role
                                        ? `, ${getPerformerPractitioner('biometrist').role}`
                                        : ''
                                })`}
                            />
                            <ChangeServiceProvider
                                currentOrganizationId={getPerformerPractitioner('biometrist').organizationId!}
                                currentPerformerId={getPerformerPractitioner('biometrist').id!}
                                dateSent={checkIsActivityEnded() ? process.dateSent : undefined}
                                diagnosticReportId={process.diagnosticReports[0] && process.diagnosticReports[0].id}
                                procedureId={process.procedure && process.procedure.id}
                                reload={async () => await load()}
                                requestId={process.id}
                                // eslint-disable-next-line jsx-a11y/aria-role
                                role="biograder"
                                serviceId={props.serviceId}
                            />
                        </>
                    )}
                    {process.task?.type === TaskType.supervise &&
                        process.task.performerPractitionerId &&
                        process.task.performerPractitioner && (
                            <>
                                <ThirdHeader
                                    text={`${translate.supervisors.supervisor}
                            (${getDisplayName(process.task.performerPractitioner)},
                            ${
                                process.taskPerformerProfession
                                    ? `, ${getProfessionTranslation(process.taskPerformerProfession)}`
                                    : ''
                            })`}
                                />
                                <ChangeServiceSupervisor
                                    currentSupervisorId={process.task.performerPractitionerId}
                                    reload={async () => await load()}
                                    serviceId={props.serviceId}
                                    taskId={process.task.id}
                                />
                            </>
                        )}
                </>
            )}
        </Modal>
    );

    function getPerformerPractitioner(role: 'biometrist' | 'grader') {
        if (
            process &&
            process.diagnosticReports[0] &&
            process.diagnosticReports[0].performerPractitioner &&
            role === 'grader'
        ) {
            return {
                name: getDisplayName(process.diagnosticReports[0].performerPractitioner!),
                role: getProfessionTranslation(process.reportPerformerProfession),
                id: process.diagnosticReports[0].performerPractitionerId,
                organizationId: process.diagnosticReports[0].performerOrganizationId,
            };
        } else if (process && process.procedure && process.procedure.performerPractitioner && role === 'biometrist') {
            return {
                name: getDisplayName(process.procedure.performerPractitioner),
                role: getProfessionTranslation(process.procedurePerformerProfession),
                id: process.procedure.performerPractitionerId,
                organizationId: process.procedure.performerOrganizationId,
            };
        } else if (process && process.recipientPractitioner && process.recipientOrganization) {
            if (process.recipientPractitioner.nameGiven !== null) {
                return {
                    name: getDisplayName(process.recipientPractitioner),
                    role: getProfessionTranslation(process.recipientPractitionerProfession),
                    id: process.recipientPractitionerId,
                    organizationId: process.recipientOrganizationId,
                };
            } else {
                return { name: process.recipientOrganization.name };
            }
        } else {
            throw new Error('No performer practitioner available');
        }
    }

    function hasServiceProviderForGrading() {
        if (process) {
            if (
                process.diagnosticReports.length > 0 &&
                process.serviceType === ServiceType.examination &&
                process.procedure &&
                process.procedure.status === ProcedureStatus.inProgress
            ) {
                return false;
            }
            if (
                process.diagnosticReports.length > 0 &&
                (process.serviceType === ServiceType.consultation || process.serviceType === ServiceType.examination)
            ) {
                return true;
            }
            if (
                process.serviceType === ServiceType.consultation &&
                [RequestStatus.requested, RequestStatus.accepted].includes(process.status) &&
                process.recipientPractitioner
            ) {
                return true;
            }
            if (
                process.serviceType === ServiceType.examination &&
                process.procedure &&
                process.procedure.status === ProcedureStatus.completed &&
                process.recipientPractitioner
            ) {
                return true;
            }
        }

        return false;
    }

    function getHeaderText() {
        if (!process || !process.requesterPractitioner) {
            return '';
        }
        if (process.requesterPractitionerId !== null) {
            if (process.requesterPractitionerProfession) {
                return `${translate.request.requester} (${getDisplayName(
                    process.requesterPractitioner
                )}, ${getProfessionTranslation(process.requesterPractitionerProfession)})`;
            } else {
                return `${translate.request.requester} (${getDisplayName(process.requesterPractitioner)})`;
            }
        } else if (
            process.requesterOrganization &&
            process.requesterOrganization.city &&
            process.recipientOrganization
        ) {
            return `${translate.request.requester} (${process.requesterOrganization.name}, ${process.recipientOrganization.city})`;
        } else if (process.requesterOrganization) {
            return `${translate.request.requester} (${process.requesterOrganization.name})`;
        }

        return '';
    }

    function getProfessionTranslation(profession?: string | null) {
        if (profession) {
            return translate.professionEnum[profession as keyof typeof translate.professionEnum];
        }
    }

    function canChangeBiometrist() {
        if (process) {
            if (process.diagnosticReports.length === 0) {
                if (
                    process.procedure ||
                    (process.status === RequestStatus.requested &&
                        process.serviceType === ServiceType.examination &&
                        process.recipientPractitioner)
                ) {
                    return true;
                }
            }
            if (process.diagnosticReports.length > 0) {
                if (
                    process.serviceType === ServiceType.examination &&
                    process.procedure &&
                    process.procedure.status !== ProcedureStatus.inProgress
                ) {
                    return true;
                }
            }
        }

        return false;
    }

    function canChangeBiograder() {
        if (process) {
            if (process.procedure && process.diagnosticReports.length > 0) {
                if (
                    process.procedure.status === ProcedureStatus.inProgress &&
                    process.diagnosticReports[0].reviewStatus === ReportStatus.draft
                ) {
                    return true;
                }
            }
        }

        return false;
    }

    async function load() {
        const result = await fetchJson(`/api/requestProcesses/${props.requestId}`, 'GET');
        setProcess(result.json);
    }

    function checkIsActivityEnded() {
        return process?.status && canChangeCareProvider(process);
    }

    function showRequestActiveAlert() {
        const message = (() => {
            if ([ReportStatus.draft, ReportStatus.open].includes(reviewStatus as ReportStatus)) {
                return translate.request.processAlreadyBeingProcessed;
            }

            if (hasDiagnosticReport) {
                return translate.request.processAlreadyCompleted;
            }
        })();

        if (message) {
            return (
                <Alert
                    appearance="warning"
                    hasIcon={true}
                    noMargin={true}
                >
                    {message}
                </Alert>
            );
        }

        return null;
    }
}
